/**
 * Cliente API + SSE para los endpoints de agentes (Sprint AI-1.d / 1.d.b).
 *
 *   - estimateSectionDraft  → POST /draft/estimate (JSON)
 *   - streamSectionDraft    → POST /draft/stream (SSE; async iterator)
 *   - proposeStructure      → POST /propose-structure (SSE; resultado en `done`)
 *   - streamAllSectionsDraft → POST /draft-all/stream (SSE; async iterator)
 *
 * Patrón de firma alineado con el resto de los clientes: el primer arg
 * es el `accessToken` y los siguientes son los argumentos del endpoint.
 *
 * Los flujos de IA son SSE (OT-0040) y se leen con `apiStream` del cliente
 * HTTP.
 *
 * Visibilidad de costos: el backend NO devuelve `*_cost_usd` al user
 * regular (Sprint AI-1.d.b). Solo tokens. El UI nunca debe mostrar USD.
 */

import {
  ApiError,
  type ApiErrorBody,
  apiFetch,
  apiStream,
  apiStreamResult,
  type SseEvent,
} from './client'
import type { CitationClaim, GroundingReport } from './citations'

// ─── Tipos ────────────────────────────────────────────────────────────────
export interface DraftEstimate {
  estimated_input_tokens: number
  estimated_output_tokens: number
  estimated_total_tokens: number
  profile_name: string
  models_used: Record<string, string>
  notes: string[]
}

export interface Citation {
  chunk_id: string
  /** `null` cuando la cita viene del canon del proyecto (no de un
   * archivo source subido). En ese caso, la identidad del artefacto
   * canon vive en `artifact_id` / `version_id` / `section_id`. */
  source_id: string | null
  scope: string
  project_id: string | null
  chunk_index: number
  section_path: string[]
  page_number: number | null
  score: number
  rerank_score: number | null
  filename: string | null
  /** `"canon"` para citas de documentos oficiales del proyecto;
   * `"source"` (o variantes) para citas de archivos subidos. La UI
   * lo usa para mostrar un badge "Oficial" sobre la cita. */
  origin: string | null
  preview: string
  /** Solo presente cuando `origin === "canon"`. Identifica el
   * artefacto oficial del que vino el chunk. */
  artifact_id?: string
  /** Solo presente cuando `origin === "canon"`. Identifica la versión
   * exacta del artefacto canon que produjo el chunk. */
  version_id?: string
  /** Solo presente cuando `origin === "canon"`. Sección dentro del
   * artefacto canon (e.g. `"intro"`, `"alcance"`). */
  section_id?: string
  /** OT-0049 — oraciones que usan esta cita, con su frase y estado. */
  evidence?: CitationClaim[]
}

export interface StepMetric {
  name: string
  role: string | null
  model_id: string | null
  latency_ms: number
  input_tokens: number
  output_tokens: number
  cached_input_tokens: number
}

export interface AgentMetrics {
  total_latency_ms: number
  total_input_tokens: number
  total_output_tokens: number
  total_cached_input_tokens: number
  total_tokens: number
  revise_iterations: number
  steps: StepMetric[]
}

export interface SectionDraftResult {
  /** OT-0049 — id de la corrida guardada en el servidor. Al aceptar el
   * texto, las citas y el juicio se guardan con este id
   * (`applySectionDraftRunCitations`); el cliente nunca manda las citas. */
  draft_run_id: string
  /** OT-0049 — juicio por oración: oraciones sin respaldo. `null` si la
   * sección no pasa por el juez (código, diagramas). */
  grounding: GroundingReport | null
  draft_text: string
  section_id: string
  section_title: string
  language: string
  citations: Citation[]
  metrics: AgentMetrics | null
  warnings: string[]
  /** ADR-0002 Fase 10.f UX fix — warnings estructurados del resolver de
   * `context_sections`. Cada item es `{code, ref, detail}`. Códigos
   * cross-ref: `cross_ref_draft_source`, `cross_ref_empty_section`, etc.
   * Para warnings free-text del resolver intra: code `intra_resolver_warning`
   * con `detail` poblado. NO se mezclan con `warnings` (token resolver). */
  context_warnings?: ContextWarning[]
  /** Tokens {{section_id.value}} que el backend sustituyó en el
   * user_instructions antes de invocar al agente. Vacío si el user no
   * mandó tokens. Solo lo retorna el endpoint no-streaming; en streaming
   * se emite como evento `tokens_resolved`. */
  tokens_resolved?: ResolvedTokenInfo[]
  /** Secciones declaradas en `template.context_sections` que se
   * inyectaron al prompt como contexto interno. Solo lo retorna el
   * endpoint no-streaming; en streaming se emite como evento
   * `context_loaded`. */
  context_sections_loaded?: LoadedContextSection[]
}

/** ADR-0002 Fase 10.f UX fix — un warning estructurado del resolver de
 * `context_sections`. El frontend lo localiza con `code` + opcionalmente
 * muestra el `ref` (cross-ref que lo generó). Si `code === 'intra_resolver_warning'`,
 * usa `detail` como mensaje crudo (free-text del resolver).
 *
 * `source_template_name` y `source_section_title` (solo cross-refs) son
 * labels localizados del template y sección referenciados — el frontend
 * prefiere mostrarlos sobre el ref técnico. `null` si el snapshot no
 * tiene i18n del template ajeno. */
export interface ContextWarning {
  /** Código estable. Cross-ref: `cross_ref_*`. Intra: `intra_resolver_warning`. */
  code: string
  /** Ref que generó el warning (solo cross-refs), ej. "tpl-discovery-brief.problem_context". */
  ref: string | null
  /** Mensaje free-text — solo para warnings de intra resolver. */
  detail: string | null
  /** Nombre legible del template referenciado, ya localizado. */
  source_template_name?: string | null
  /** Título legible de la sección consumida, ya localizado. */
  source_section_title?: string | null
}

/** Un token sustituido en el user_instructions por el backend. */
export interface ResolvedTokenInfo {
  /** Texto original del token, ej. "{{caso_de_uso.value}}". */
  raw: string
  section_id: string
  field: string
  /** True si el resultado fue un array (caller fuera del repetible). */
  is_array: boolean
  /** Longitud (chars) del valor sustituido — útil para advertir
   * "este token explota el contexto" cuando es muy grande. */
  char_count: number
}

/** Una sección del template inyectada como `context_sections` (Sub-fase
 * 2.1++). El template author la declaró como dependencia automática y
 * el backend la cargó del current_version.content. */
export interface LoadedContextSection {
  section_id: string
  /** Título localizado de la sección (i18n del template). */
  title: string
  is_array: boolean
  char_count: number
  /** Bug fix (2026-05-06): contenido real que se inyectó al prompt
   * (truncado a ~800 chars). Permite al user verificar que el contexto
   * declarado en el template está llegando correctamente al LLM
   * — útil cuando la IA ignora el contexto y la duda es si lo recibió. */
  rendered_preview?: string
}

// ─── Eventos del stream (matchea AgentEventType del backend) ─────────────
/** Latido `waiting` de los streams de IA (OT-0040): el backend lo manda
 * cada 15 s sin otro evento, con el paso en curso (si hay) y los segundos
 * desde el último evento. */
export interface WaitingPayload {
  step: string | null
  elapsed_s: number
}

export type DraftStreamEvent =
  | {
      type: 'tokens_resolved'
      payload: {
        resolved: ResolvedTokenInfo[]
        warnings: string[]
        /** ADR-0002 Fase 10.f UX fix — warnings estructurados del resolver
         * de `context_sections`. Optional para retro-compat con backends
         * pre-fix (campo nuevo). */
        context_warnings?: ContextWarning[]
      }
    }
  | {
      type: 'context_loaded'
      payload: {
        sections: LoadedContextSection[]
      }
    }
  | { type: 'step_start'; payload: { step: string; role: string | null } }
  | {
      type: 'step_complete'
      payload: {
        step: string
        role: string | null
        model_id?: string | null
        latency_ms?: number
        input_tokens: number
        output_tokens: number
        cached_input_tokens?: number
      }
    }
  | {
      type: 'retrieve_done'
      payload: {
        chunks_count: number
        sources_count: number
        /** Doc 22 Fase 8.k — chunks que vinieron del canon del
         * proyecto (documentos oficiales ya aprobados) versus de
         * archivos source subidos. `chunks_count` los incluye. */
        canon_chunks_count?: number
        /** Doc 22 Fase 8.k — artefactos canon distintos que aportaron
         * chunks. `sources_count` ya los suma a las fuentes uploaded. */
        canon_sources_count?: number
        queries_count: number
      }
    }
  | {
      type: 'critique_result'
      payload: {
        passes: boolean
        issues_count: number
        suggestions_count: number
        /** Primeras 3 issues textuales, truncadas a 200 chars c/u
         * (Sprint AI-1.d.b — diagnóstico en vivo de qué dice el critic). */
        issues?: string[]
        suggestions?: string[]
      }
    }
  | { type: 'warning'; payload: { code: string; message: string } }
  | { type: 'waiting'; payload: WaitingPayload }
  | { type: 'done'; payload: SectionDraftResult }
  | {
      type: 'error'
      payload: { code?: string; step?: string | null; message: string }
    }

/** Un segmento del path activo desde root al caller. Cuando `index`
 * está presente, indica que estamos dentro de la instancia índice
 * `index` del repetible cuyo array vive bajo `section_id` (convención
 * HIVA: el contenedor padre lleva el array, el repeatable singular es
 * implícito). El último segmento (la sección hoja) NO lleva index. */
export interface CurrentPathSegment {
  section_id: string
  index?: number
}

export interface DraftRequestBody {
  profile_name?: string | null
  /** Tipo de sección del template (rich_text, bullet_list, ...). El
   * agente adapta el formato del output según este valor — sin él,
   * genera markdown libre y las listas salen mal formateadas
   * (preámbulos + 'Source:' como items). */
  section_type?: string | null
  /** (Modo 2.1) Texto libre del usuario inyectado al system prompt
   * como bloque de PRIORIDAD ALTA. Útil para dirigir al agente:
   *   "usa la sección X como fuente"
   *   "incluye los flujos alternos del CU-001"
   *   "enfatiza ISO 27001". */
  user_instructions?: string | null
  /** (Modo 2.1) Contenido actual de la sección si el user marcó la
   * opción "tomar como referencia". Se inyecta como bloque "EXISTING
   * CONTENT" — el agente debe construir sobre ello, no descartarlo. */
  current_content?: unknown
  /** (Sprint AI-1.f) Path desde root al caller con índices de
   * instancias activas. Permite al backend resolver tokens y
   * `context_sections` con la INSTANCIA activa (casos 2/3) en lugar
   * de array completo (caso 4). Vacío = sección está en root. */
  current_path?: CurrentPathSegment[]
}

// ─── Estimate (JSON) ──────────────────────────────────────────────────────
export async function estimateSectionDraft(
  accessToken: string,
  artifactId: string,
  sectionId: string,
  options?: {
    profileName?: string | null
    sectionType?: string | null
    versionId?: string | null
  },
): Promise<DraftEstimate> {
  const qs = options?.versionId ? `?version_id=${options.versionId}` : ''
  return apiFetch<DraftEstimate>(
    `/api/v1/artifacts/${artifactId}/sections/${sectionId}/draft/estimate${qs}`,
    {
      method: 'POST',
      accessToken,
      body: {
        profile_name: options?.profileName ?? null,
        section_type: options?.sectionType ?? null,
      } satisfies DraftRequestBody,
    },
  )
}

// ─── SSE stream (async iterator) ─────────────────────────────────────────
/**
 * Abre la conexión SSE contra `/draft/stream` y yield cada evento parseado
 * conforme llega. El consumer típico:
 *
 *   const ctrl = new AbortController()
 *   for await (const evt of streamSectionDraft(token, aid, sid, { signal: ctrl.signal })) {
 *     // procesa evt según evt.type
 *     if (evt.type === 'done' || evt.type === 'error') break
 *   }
 *
 * El cliente puede abortar cancelando con `ctrl.abort()`.
 *
 * Levanta `ApiError` si la HTTP request inicial falla (404, 401, 402,
 * etc.). Errores **dentro** del stream llegan como evento `error` —
 * NO como excepción.
 */
export async function* streamSectionDraft(
  accessToken: string,
  artifactId: string,
  sectionId: string,
  options?: {
    profileName?: string | null
    sectionType?: string | null
    userInstructions?: string | null
    currentContent?: unknown
    currentPath?: CurrentPathSegment[]
    versionId?: string | null
    signal?: AbortSignal
  },
): AsyncGenerator<DraftStreamEvent, void, void> {
  const qs = options?.versionId ? `?version_id=${options.versionId}` : ''
  yield* apiStream<DraftStreamEvent>(
    `/api/v1/artifacts/${artifactId}/sections/${sectionId}/draft/stream${qs}`,
    {
      accessToken,
      body: {
        profile_name: options?.profileName ?? null,
        section_type: options?.sectionType ?? null,
        user_instructions: options?.userInstructions ?? null,
        current_content: options?.currentContent ?? null,
        current_path: options?.currentPath ?? [],
      } satisfies DraftRequestBody,
      signal: options?.signal,
      terminal: ['done', 'error'],
    },
  )
}

// ─── StructureAgent (Sprint AI-1.g — Sub-fase 5.2) ──────────────────────
/** Body para `POST /artifacts/{id}/propose-structure`. */
export interface ProposeStructureRequestBody {
  /** Texto libre — el agente lo respeta literalmente si pide un número
   * específico (ej. "propón 5 casos de uso"). Si vacío o vago, el
   * agente decide N basándose en sources. */
  user_hint?: string | null
  profile_name?: string | null
}

export interface ProposeStructureEstimate {
  repeatable_count: number
  estimated_input_tokens: number
  estimated_output_tokens: number
  estimated_total_tokens: number
  profile_name: string
  notes: string[]
}

export interface MergeSummary {
  /** container_section_id → cuántos items NUEVOS se proponen agregar. */
  instances_added: Record<string, number>
  /** container_section_id → cuántos items ya existentes se preservan. */
  instances_kept: Record<string, number>
  /** Paths de campos individuales que el merge respetó (no sobreescribió). */
  fields_skipped: string[]
}

export interface ProposeStructureResult {
  /** El JSON con las instancias NUEVAS propuestas (no incluye lo
   * existente). Útil para mostrar "estas son las propuestas nuevas". */
  proposal: Record<string, unknown>
  /** Justificación corta del LLM en lenguaje natural. */
  rationale: string
  /** El content COMPLETO listo para guardar — current + proposal merged
   * aditivamente. El frontend lo manda al `PUT /artifacts/{id}/content`
   * cuando el user acepta. */
  final_proposal: Record<string, unknown>
  merge_summary: MergeSummary
  rag_citations: Citation[]
  metrics: AgentMetrics | null
  warnings: string[]
}

export async function estimateProposeStructure(
  accessToken: string,
  artifactId: string,
  options?: { versionId?: string | null },
): Promise<ProposeStructureEstimate> {
  const qs = options?.versionId ? `?version_id=${options.versionId}` : ''
  return apiFetch<ProposeStructureEstimate>(
    `/api/v1/artifacts/${artifactId}/propose-structure/estimate${qs}`,
    { method: 'POST', accessToken },
  )
}

/**
 * Corre el StructureAgent (SSE, OT-0040) y resuelve con el resultado del
 * evento `done`. `onEvent` recibe el progreso (pasos, `waiting`); un evento
 * `error` se lanza como `ApiError` con su `code`.
 */
export async function proposeStructure(
  accessToken: string,
  artifactId: string,
  body?: ProposeStructureRequestBody,
  options?: {
    versionId?: string | null
    signal?: AbortSignal
    onEvent?: (event: SseEvent) => void
  },
): Promise<ProposeStructureResult> {
  const qs = options?.versionId ? `?version_id=${options.versionId}` : ''
  return apiStreamResult<ProposeStructureResult>(
    apiStream(`/api/v1/artifacts/${artifactId}/propose-structure${qs}`, {
      accessToken,
      body: {
        user_hint: body?.user_hint ?? null,
        profile_name: body?.profile_name ?? null,
      } satisfies ProposeStructureRequestBody,
      signal: options?.signal,
      terminal: ['done', 'error'],
    }),
    options?.onEvent,
  )
}

// ─── Generate-all orchestrator (Sprint AI-1.g — Sub-fase 5.3) ───────────
/** Body para `POST /artifacts/{id}/draft-all/stream`. */
export interface DraftAllRequestBody {
  /** Si true, antes de generar contenido corre el StructureAgent para
   * llenar instancias de repetibles que falten. Default false. */
  create_structure?: boolean
  /** Si false (default), salta secciones que ya tienen contenido. Si
   * true, regenera todo. */
  overwrite_existing?: boolean
  /** Texto libre para el StructureAgent — solo aplica si
   * `create_structure=true`. */
  structure_hint?: string | null
  profile_name?: string | null
}

/** Pre-flight estimate del orchestrator (sin LLM). Devuelve rangos
 * min/max porque el output real varía por sección. */
export interface DraftAllEstimate {
  editable_sections_total: number
  already_written: number
  to_generate_min: number
  to_generate_max: number
  estimated_input_tokens_min: number
  estimated_input_tokens_max: number
  estimated_output_tokens_min: number
  estimated_output_tokens_max: number
  estimated_total_tokens_min: number
  estimated_total_tokens_max: number
  repeatable_groups: number
  notes: string[]
}

/** Eventos del SSE stream del orchestrator. Matchea el dict que el
 * service yield-ea en `agent_drafting_service.draft_all_streaming`. */
export type DraftAllStreamEvent =
  | { type: 'waiting'; payload: WaitingPayload }
  | {
      type: 'orchestrator_start'
      payload: {
        create_structure: boolean
        overwrite_existing: boolean
        artifact_template: string
      }
    }
  | { type: 'structure_phase_start'; payload: Record<string, never> }
  | {
      type: 'structure_phase_done'
      payload: {
        instances_added: Record<string, number>
        rationale: string
      }
    }
  | {
      type: 'structure_phase_failed'
      payload: { code: string; message: string }
    }
  | {
      type: 'topology_built'
      payload: {
        total: number
        skipped_existing: number
        skipped_non_editable: number
        topo_warnings: string[]
      }
    }
  | {
      type: 'section_skipped'
      payload: {
        section_id: string
        instance_key: string
        reason: string
      }
    }
  | {
      type: 'section_start'
      payload: {
        index: number
        total: number
        section_id: string
        instance_key: string
        section_type: string
      }
    }
  | {
      type: 'section_done'
      payload: {
        section_id: string
        instance_key: string
        draft_chars: number
        tokens_input: number
        tokens_output: number
        latency_ms: number
      }
    }
  | {
      type: 'section_error'
      payload: {
        section_id: string
        instance_key: string
        code: string
        message: string
      }
    }
  | {
      type: 'orchestrator_done'
      payload: {
        total: number
        succeeded: number
        failed: number
        skipped_existing: number
        skipped_non_editable: number
        aggregate_metrics: {
          input_tokens: number
          output_tokens: number
          total_tokens: number
          latency_ms: number
        }
      }
    }
  | {
      type: 'error'
      payload: { code?: string; message: string }
    }

export async function estimateDraftAll(
  accessToken: string,
  artifactId: string,
  options?: { versionId?: string | null },
): Promise<DraftAllEstimate> {
  const qs = options?.versionId ? `?version_id=${options.versionId}` : ''
  return apiFetch<DraftAllEstimate>(
    `/api/v1/artifacts/${artifactId}/draft-all/estimate${qs}`,
    { method: 'POST', accessToken },
  )
}

/**
 * SSE consumer del orchestrator (`apiStream`, igual que
 * `streamSectionDraft`). El stream es largo (puede correr 5-15 minutos según
 * cuántas secciones tenga el artifact), así que el caller DEBE
 * proveer un `signal` de AbortController para permitir cancelar.
 *
 * Errores HTTP iniciales (404, 401, 402) se levantan como `ApiError`.
 * Errores DENTRO del stream llegan como evento `error` (el server
 * envuelve excepciones en un evento SSE estándar).
 */
export async function* streamAllSectionsDraft(
  accessToken: string,
  artifactId: string,
  options?: DraftAllRequestBody & {
    signal?: AbortSignal
    versionId?: string | null
  },
): AsyncGenerator<DraftAllStreamEvent, void, void> {
  const qs = options?.versionId ? `?version_id=${options.versionId}` : ''
  yield* apiStream<DraftAllStreamEvent>(
    `/api/v1/artifacts/${artifactId}/draft-all/stream${qs}`,
    {
      accessToken,
      body: {
        create_structure: options?.create_structure ?? false,
        overwrite_existing: options?.overwrite_existing ?? false,
        structure_hint: options?.structure_hint ?? null,
        profile_name: options?.profile_name ?? null,
      } satisfies DraftAllRequestBody,
      signal: options?.signal,
      terminal: ['orchestrator_done', 'error'],
    },
  )
}

export { ApiError, type ApiErrorBody }
